import { Layout } from "@/components/layout/layout";
import {
  useGetDashboardStats,
  useListMyRegistrations,
  getListMyRegistrationsQueryKey,
} from "@workspace/api-client-react";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { TournamentCard } from "@/components/tournament-card";
import { ArrowRight, Trophy, Users, MapPin, CheckCircle2 } from "lucide-react";
import { formatMoney } from "@/lib/format";
import heroBg from "@/assets/hero-bg.png";
import logoPath from "@/assets/poker-logo.png";
import { useMemo } from "react";
import { useCurrentPlayer } from "@/hooks/useCurrentPlayer";
import { useSeo } from "@/lib/seo";

export default function Home() {
  useSeo({
    fullTitle: true,
    title:
      "Interclub Poker Circuit — Pub Poker Tournaments, Leagues & Leaderboards",
    description:
      "Find pub poker tournaments near you, RSVP to upcoming games, track league leaderboards and season standings across the Interclub Poker Circuit.",
    canonicalPath: "/",
  });
  const { data: stats, isLoading } = useGetDashboardStats();
  const { player } = useCurrentPlayer();
  const { data: myRegistrations } = useListMyRegistrations({
    query: { enabled: !!player, queryKey: getListMyRegistrationsQueryKey() },
  });
  const registeredTournamentIds = useMemo(() => {
    const set = new Set<number>();
    if (!player) return set;
    for (const r of myRegistrations ?? []) {
      if (r.registrationStatus === "registered" || r.registrationStatus === "late") {
        set.add(r.tournamentId);
      }
    }
    return set;
  }, [myRegistrations, player]);

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative w-full overflow-hidden bg-background">
        <div className="absolute inset-0 z-0">
          <img 
            src={heroBg} 
            alt="Warm low-light pub poker table" 
            className="w-full h-full object-cover opacity-30 mix-blend-overlay"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/80 to-background/20" />
          <div className="absolute inset-0 bg-gradient-to-r from-background via-background/60 to-transparent" />
        </div>

        <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-24 lg:pt-48 lg:pb-32">
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(280px,460px)] gap-12 lg:gap-16 items-center">
            <div className="max-w-3xl">
              <div className="inline-flex items-center rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-sm font-medium text-primary mb-6">
                <span className="flex h-2 w-2 rounded-full bg-primary mr-2 animate-pulse"></span>
                The premier Aussie pub-poker circuit
              </div>
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight text-foreground mb-6 leading-[1.1]">
                Real poker.<br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-amber-500">Real stakes.</span><br />
                At your local.
              </h1>
              <p className="text-lg sm:text-xl text-muted-foreground mb-10 max-w-2xl leading-relaxed">
                Find tournaments hosted by your local pubs and clubs in every state — NSW, VIC, QLD, WA, SA, TAS, and the NT. Cash buy-ins and free freeroll games, run by the venues themselves with well-designed blind structures and guaranteed prize pools.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link href="/tournaments">
                  <Button size="lg" className="w-full sm:w-auto h-14 px-8 text-base font-semibold bg-primary text-primary-foreground hover:bg-primary/90">
                    Find a Game
                  </Button>
                </Link>
                <Link href="/signup">
                  <Button size="lg" variant="outline" className="w-full sm:w-auto h-14 px-8 text-base font-semibold border-primary/50 text-foreground hover:bg-primary/10">
                    Join the Circuit
                  </Button>
                </Link>
              </div>
            </div>

            <div className="relative flex items-center justify-center order-first lg:order-last">
              <div className="absolute inset-0 -z-10 bg-primary/20 blur-3xl rounded-full" aria-hidden />
              <img
                src={logoPath}
                alt="Interclub Poker Circuit"
                className="w-56 sm:w-72 lg:w-full h-auto object-contain drop-shadow-[0_18px_45px_rgba(212,175,55,0.45)]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features/Vibe */}
      <section className="py-24 bg-background">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            <div className="space-y-4">
              <div className="h-12 w-12 rounded-lg bg-secondary/20 flex items-center justify-center text-secondary">
                <Trophy className="h-6 w-6" />
              </div>
              <h3 className="text-xl font-bold text-foreground">Serious Structure</h3>
              <p className="text-muted-foreground leading-relaxed">
                Slow blind levels, deep starting stacks, and well-balanced payout structures. We respect your time and your game.
              </p>
            </div>
            <div className="space-y-4">
              <div className="h-12 w-12 rounded-lg bg-accent/20 flex items-center justify-center text-accent">
                <MapPin className="h-6 w-6" />
              </div>
              <h3 className="text-xl font-bold text-foreground">Hosted by your local</h3>
              <p className="text-muted-foreground leading-relaxed">
                Every game is run by an actual pub or club in your area. Great food, cold taps, and a dedicated tournament space.
              </p>
            </div>
            <div className="space-y-4">
              <div className="h-12 w-12 rounded-lg bg-primary/20 flex items-center justify-center text-primary">
                <CheckCircle2 className="h-6 w-6" />
              </div>
              <h3 className="text-xl font-bold text-foreground">In-house staff</h3>
              <p className="text-muted-foreground leading-relaxed">
                Tournaments are run by the venue's own tournament directors and floor staff — people who know their regulars and their room.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Upcoming Tournaments */}
      <section className="py-24 bg-card border-t border-border">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-end mb-10">
            <div>
              <h2 className="text-3xl font-bold text-foreground tracking-tight">Next on the Felt</h2>
              <p className="mt-2 text-muted-foreground">Upcoming featured tournaments across the circuit.</p>
            </div>
            <Link href="/tournaments" className="hidden sm:flex items-center text-primary font-medium hover:text-primary/80 transition-colors">
              View all Schedule <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {isLoading ? (
              Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="h-64 rounded-xl bg-muted/20 animate-pulse border border-border" />
              ))
            ) : (
              stats?.upcomingTournaments && stats.recentTournaments.filter(t => t.status === "upcoming").slice(0, 3).map((tournament) => (
                <TournamentCard
                  key={tournament.id}
                  tournament={tournament}
                  isRegistered={registeredTournamentIds.has(tournament.id)}
                />
              ))
            )}
          </div>
          
          <div className="mt-8 sm:hidden">
            <Link href="/tournaments">
              <Button variant="outline" className="w-full">View Full Schedule</Button>
            </Link>
          </div>
        </div>
      </section>
      
      {/* Top Players Preview */}
      <section className="py-24 bg-background border-t border-border">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-end mb-10">
            <div>
              <h2 className="text-3xl font-bold text-foreground tracking-tight">Circuit Leaders</h2>
              <p className="mt-2 text-muted-foreground">The current top dogs. Can you catch them?</p>
            </div>
            <Link href="/leaderboard" className="hidden sm:flex items-center text-primary font-medium hover:text-primary/80 transition-colors">
              Full Leaderboard <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {isLoading ? (
              Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="h-32 rounded-xl bg-muted/20 animate-pulse border border-border" />
              ))
            ) : (
              stats?.topPlayers.slice(0, 4).map((player, idx) => (
                <div key={player.id} className="p-6 rounded-xl bg-card border border-border flex flex-col items-center text-center hover:border-primary/50 transition-colors">
                  <div className="w-14 h-14 rounded-full bg-secondary/20 text-secondary flex items-center justify-center font-bold text-xl mb-4 relative">
                    {player.name.charAt(0)}
                    {idx === 0 && (
                      <div className="absolute -top-2 -right-2 bg-primary text-primary-foreground text-[10px] w-6 h-6 rounded-full flex items-center justify-center font-bold">
                        1
                      </div>
                    )}
                  </div>
                  <h3 className="font-bold text-foreground">{player.name}</h3>
                  <div className="mt-2 text-sm text-primary font-medium">
                    {player.totalPoints.toLocaleString()} pts
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {formatMoney(player.totalWinnings)}
                  </div>
                </div>
              ))
            )}
          </div>
          
          <div className="mt-8 sm:hidden">
            <Link href="/leaderboard">
              <Button variant="outline" className="w-full">Full Leaderboard</Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-secondary relative overflow-hidden">
        <div className="absolute inset-0 opacity-10 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI4IiBoZWlnaHQ9IjgiPgo8cmVjdCB3aWR0aD0iOCIgaGVpZ2h0PSI4IiBmaWxsPSIjZmZmIiBmaWxsLW9wYWNpdHk9IjAuMSI+PC9yZWN0Pgo8cGF0aCBkPSJNMCAwTDggOFpNOCAwTDAgOFoiIHN0cm9rZT0iIzAwMCIgc3Ryb2tlLW9wYWNpdHk9IjAuMSI+PC9wYXRoPgo8L3N2Zz4=')]"></div>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
          <h2 className="text-3xl sm:text-5xl font-bold text-secondary-foreground mb-6">Ready to take a seat?</h2>
          <p className="text-lg text-secondary-foreground/80 max-w-2xl mx-auto mb-10">
            Join thousands of players in the fastest growing pub-poker circuit in Australia. Create your player profile to register for tournaments and track your stats.
          </p>
          <Link href="/signup">
            <Button size="lg" className="h-14 px-10 text-lg font-bold bg-primary text-primary-foreground hover:bg-primary/90">
              Create Player Profile
            </Button>
          </Link>
        </div>
      </section>
    </Layout>
  );
}
